"use client";

import { NPS_COMPLETED_KEY, NPS_SESSION_CHANGED_EVENT } from "@/lib/nps/constants";
import { hasPriorDayNpsSession } from "@/lib/nps/prior-day-session";
import { readNpsTimerStartedAt } from "@/lib/nps/session";
import { useEffect, useState } from "react";

const NPS_TIMER_DURATION = 15 * 60 * 1000; // 15 minutes

interface UseNPSTimerReturn {
  shouldShowNPS: boolean;
  persistNpsCompleted: () => void;
  dismissNPS: () => void;
}

function wasNpsCompleted(): boolean {
  try {
    return localStorage.getItem(NPS_COMPLETED_KEY) === "true";
  } catch {
    return false;
  }
}

export function useNPSTimer(): UseNPSTimerReturn {
  const [shouldShowNPS, setShouldShowNPS] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const clearTimer = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    const evaluate = () => {
      clearTimer();

      // Everyone shares the same timing/visit eligibility. Audience is learned in the survey.
      if (wasNpsCompleted() || !hasPriorDayNpsSession()) {
        setShouldShowNPS(false);
        return;
      }

      const startedAt = readNpsTimerStartedAt();
      if (startedAt == null) {
        setShouldShowNPS(false);
        return;
      }

      const remaining = NPS_TIMER_DURATION - (Date.now() - startedAt);
      if (remaining <= 0) {
        setShouldShowNPS(true);
        return;
      }

      setShouldShowNPS(false);
      timer = setTimeout(() => {
        if (wasNpsCompleted() || !hasPriorDayNpsSession()) {
          setShouldShowNPS(false);
          return;
        }
        setShouldShowNPS(true);
      }, remaining);
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === NPS_COMPLETED_KEY) evaluate();
    };

    window.addEventListener(NPS_SESSION_CHANGED_EVENT, evaluate);
    window.addEventListener("storage", onStorage);
    evaluate();

    return () => {
      clearTimer();
      window.removeEventListener(NPS_SESSION_CHANGED_EVENT, evaluate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const persistNpsCompleted = () => {
    try {
      localStorage.setItem(NPS_COMPLETED_KEY, "true");
    } catch (error) {
      console.error("Error persisting NPS completion", { error });
    }
  };

  const dismissNPS = () => {
    persistNpsCompleted();
    // Always hide even if storage is unavailable — otherwise dismiss appears broken.
    setShouldShowNPS(false);
  };

  return {
    shouldShowNPS,
    persistNpsCompleted,
    dismissNPS,
  };
}
